import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { api, ApiError, type ExternalContact } from "../../lib/api";
import { cn, useDebouncedValue } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Input, Label, Skeleton, Tip } from "../../components/ui/primitives";
import { formatWhen } from "../schedules/cadence";
import { LocationsPicker, type Place } from "../schedules/ScheduleForm";
import { ApprovedDomains } from "./ApprovedDomains";

// External contacts (docs/scheduled-reports.md, "External contacts"): email addresses that get
// scheduled reports without signing in here. Staff grant each one locations; a schedule can go to a
// contact only when it's granted every one of the schedule's locations. Addresses must be at an
// approved domain, listed here (ApprovedDomains.tsx); only super users change the list.

export function ContactsPage() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const [q, setQ] = useState("");
  const query = useDebouncedValue(q.trim(), 300);
  const contacts = useQuery({ queryKey: ["contacts", query], queryFn: () => api.contacts(query), retry: false });
  const domains = useQuery({ queryKey: ["approved-domains"], queryFn: api.approvedDomains, retry: false });
  const [editing, setEditing] = useState<ExternalContact | "new">();
  const [deleting, setDeleting] = useState<ExternalContact>();
  const staffSession = me.data?.userId.startsWith("staff:") ?? false;
  const superUser = me.data?.viewAsTier === "super";
  const forbidden = contacts.error instanceof ApiError && contacts.error.status === 403;

  return (
    <div className="mx-auto max-w-[1400px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><AtSign className="size-4 text-subtle" /> Contacts</h1>
          <span className="text-sm text-muted">
            People who get scheduled reports without signing in here. A schedule can go to one only if it's granted every one of the schedule's locations.
          </span>
        </div>
      </header>

      {forbidden ? (
        <p className="text-sm text-bad">Only GoTab staff can manage contacts.</p>
      ) : (
        <>
          <ApprovedDomains domains={domains} superUser={superUser} />
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative w-72">
              <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" aria-label="Search contacts" className="pl-8" />
            </div>
            <Button variant="primary" className="ml-auto" onClick={() => setEditing("new")} disabled={!domains.data?.length}>
              <Plus className="size-3.5" /> Add contact
            </Button>
          </div>
          <ContactTable
            query={contacts}
            empty={query ? `No contact matches “${query}”.` : "No contacts yet."}
            onEdit={setEditing}
            onDelete={setDeleting}
          />
        </>
      )}
      {editing && (
        <ContactDialog
          key={editing === "new" ? "new" : editing.id}
          contact={editing === "new" ? undefined : editing}
          domains={domains.data?.map((d) => d.domain) ?? []}
          staffSession={staffSession}
          onClose={() => setEditing(undefined)}
        />
      )}
      {deleting && <DeleteContactDialog contact={deleting} onClose={() => setDeleting(undefined)} />}
    </div>
  );
}

function ContactTable({ query, empty, onEdit, onDelete }: {
  query: { isLoading: boolean; error: Error | null; data?: ExternalContact[] };
  empty: string;
  onEdit: (c: ExternalContact) => void;
  onDelete: (c: ExternalContact) => void;
}) {
  if (query.isLoading) return <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>;
  if (query.error) return <p className="text-sm text-bad">{query.error.message}</p>;
  const rows = query.data ?? [];
  if (!rows.length) return <p className="mt-16 text-center text-sm text-muted">{empty}</p>;
  const cell = "border-b border-border px-3 py-2.5 align-top";
  return (
    <div className="overflow-x-auto rounded-[14px] border border-border bg-surface">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {["Contact", "Locations", "Changed", ""].map((h, i) => (
              <th key={i} className="border-b border-border px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-muted">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="hover:bg-surface-2/60">
              <td className={cell}>
                <span className="font-medium">{c.name ?? c.email}</span>
                {c.name && <span className="block text-xs text-subtle">{c.email}</span>}
              </td>
              <td className={cn(cell, "max-w-96")}>
                <Tip content={c.locations.map((l) => `${l.name} #${l.id}`).join(", ")}>
                  <span className="line-clamp-2">{c.locations.map((l) => l.name).join(", ")}</span>
                </Tip>
              </td>
              <td className={cn(cell, "whitespace-nowrap tabular text-muted")} title={c.updatedAt}>{formatWhen(c.updatedAt)}</td>
              <td className={cn(cell, "whitespace-nowrap text-right")}>
                <Tip content="Edit">
                  <Button size="icon-sm" variant="ghost" aria-label={`Edit ${c.email}`} onClick={() => onEdit(c)}><Pencil className="size-3.5" /></Button>
                </Tip>
                <Tip content="Delete">
                  <Button size="icon-sm" variant="ghost" aria-label={`Delete ${c.email}`} onClick={() => onDelete(c)}><Trash2 className="size-3.5" /></Button>
                </Tip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const domainOf = (email: string) => email.slice(email.lastIndexOf("@") + 1).toLowerCase();

/** Add a contact, or change one's address, name or locations. The worker checks it all again. */
function ContactDialog({ contact, domains, staffSession, onClose }: {
  contact?: ExternalContact; domains: string[]; staffSession: boolean; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(contact?.email ?? "");
  const [name, setName] = useState(contact?.name ?? "");
  const [locations, setLocations] = useState<Place[]>(contact?.locations ?? []);
  const save = useMutation({
    mutationFn: () => {
      const body = { email: email.trim(), name: name.trim() || null, locationIds: locations.map((l) => l.id) };
      return contact ? api.updateContact(contact.id, body) : api.createContact(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      void qc.invalidateQueries({ queryKey: ["schedule-recipients"] });
      onClose();
    },
  });
  const trimmed = email.trim();
  const domain = trimmed.includes("@") ? domainOf(trimmed) : "";
  const missing = !trimmed.includes("@") ? "Enter an email address"
    : !domains.includes(domain) ? `${domain || "That domain"} isn't an approved domain`
      : !locations.length ? "Choose at least one location" : undefined;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={contact ? "Edit contact" : "Add contact"} className="top-[6vh] w-[560px] max-w-[calc(100vw-32px)] max-h-[88vh] translate-y-0 overflow-y-auto p-6">
      <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight"><AtSign className="size-4 text-subtle" /> {contact ? "Edit contact" : "Add contact"}</h2>
      <p className="mt-1 mb-5 text-sm text-muted">
        They'll get the schedules they're added to, for the locations you grant here, without signing in. They see everything those reports show
        at these locations, labor included.
      </p>
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="contact-email">Email</Label>
            <Input id="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`name@${domains[0] ?? "example.com"}`} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="contact-name">Name <span className="font-normal text-subtle">(optional)</span></Label>
            <Input id="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <p className="-mt-3 text-xs text-subtle">Approved domains: {domains.join(", ")}.</p>
        <LocationsPicker
          locations={locations}
          onChange={setLocations}
          staffSession={staffSession}
          hint={contact ? "Schedules it's on that cover a location you take away stop sending to it at their next send." : "The locations whose reports this contact may get."}
        />
      </div>
      {save.error && <p className="mt-4 text-sm text-bad">{save.error.message}</p>}
      <div className="mt-6 flex items-center justify-end gap-2">
        {missing && <span className="mr-auto text-xs text-subtle">{missing}</span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!!missing || save.isPending} onClick={() => save.mutate()}>{contact ? "Save" : "Add"}</Button>
      </div>
    </Dialog>
  );
}

function DeleteContactDialog({ contact, onClose }: { contact: ExternalContact; onClose: () => void }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api.deleteContact(contact.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      void qc.invalidateQueries({ queryKey: ["schedule-recipients"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Delete contact" className="w-[440px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Delete {contact.name ?? contact.email}?</h2>
      <p className="mt-1 text-sm text-muted">
        Schedules that go to {contact.email} stop sending to it at their next send, and their owners are told. The audit log keeps a record.
      </p>
      {del.error && <p className="mt-3 text-sm text-bad">{del.error.message}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={del.isPending} onClick={() => del.mutate()}>Delete</Button>
      </div>
    </Dialog>
  );
}
